import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Timer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import errorSound from '../assets/sounds/error2.mp3';

interface PrimeQuizGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Question {
  type: 'multiple_choice' | 'true_false' | 'next_prime' | 'consecutive_primes';
  question: string;
  options?: number[] | string[];
  correctAnswer: number | number[];
  explanation?: string;
}

const PrimeQuizGame: React.FC<PrimeQuizGameProps> = ({ mode, onReset, language }) => {
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [score, setScore] = useState(0);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [remainingTime, setRemainingTime] = useState(60);
  const [userAnswer, setUserAnswer] = useState<number | number[] | null>(null);
  const [consecutiveAnswers, setConsecutiveAnswers] = useState<string[]>(['', '', '']);
  const [showFeedback, setShowFeedback] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playError] = useSound(errorSound, { volume: 0.3 });

  const isPrime = (num: number): boolean => {
    if (num <= 1) return false;
    if (num <= 3) return true;
    if (num % 2 === 0 || num % 3 === 0) return false;
    
    for (let i = 5; i * i <= num; i += 6) {
      if (num % i === 0 || num % (i + 2) === 0) return false;
    }
    return true;
  };

  const getNextPrime = (num: number): number => {
    let next = num + 1;
    while (!isPrime(next)) {
      next++;
    }
    return next;
  };

  const generateQuestion = (): Question => {
    const types = ['multiple_choice', 'true_false', 'next_prime', 'consecutive_primes'];
    const type = types[Math.floor(Math.random() * types.length)] as Question['type'];
    
    switch (type) {
      case 'multiple_choice': {
        const questionTypes = [
          {
            question: language === 'en' 
              ? 'Which of these numbers is prime?' 
              : 'Lequel de ces nombres est premier ?',
            generateOptions: () => {
              const options = [];
              const max = 50;
              while (options.length < 4) {
                const num = Math.floor(Math.random() * max) + 2;
                if (!options.includes(num)) {
                  options.push(num);
                }
              }
              const correctIndex = Math.floor(Math.random() * 4);
              while (!isPrime(options[correctIndex])) {
                options[correctIndex] = getNextPrime(options[correctIndex]);
              }
              return { options, correctAnswer: options[correctIndex] };
            }
          },
          {
            question: language === 'en'
              ? 'How many divisors does a prime number have?'
              : 'Combien de diviseurs a un nombre premier ?',
            generateOptions: () => ({
              options: [0, 1, 2, 3],
              correctAnswer: 2,
              explanation: language === 'en'
                ? 'A prime number has exactly two divisors: 1 and itself'
                : 'Un nombre premier a exactement deux diviseurs : 1 et lui-même'
            })
          },
          {
            question: language === 'en'
              ? 'Which is the smallest prime number?'
              : 'Quel est le plus petit nombre premier ?',
            generateOptions: () => ({
              options: [0, 1, 2, 3],
              correctAnswer: 2,
              explanation: language === 'en'
                ? '2 is the smallest and only even prime number'
                : '2 est le plus petit nombre premier et le seul nombre premier pair'
            })
          }
        ];

        const selectedQuestion = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const { options, correctAnswer, explanation } = selectedQuestion.generateOptions();

        return {
          type,
          question: selectedQuestion.question,
          options,
          correctAnswer,
          explanation
        };
      }

      case 'true_false': {
        const questionTypes = [
          {
            generate: () => {
              const num = Math.floor(Math.random() * 50) + 2;
              return {
                question: language === 'en'
                  ? `Is ${num} a prime number?`
                  : `${num} est-il un nombre premier ?`,
                correctAnswer: isPrime(num) ? 1 : 0,
                explanation: isPrime(num)
                  ? (language === 'en'
                      ? `${num} is prime because it has exactly two divisors: 1 and itself`
                      : `${num} est premier car il a exactement deux diviseurs : 1 et lui-même`)
                  : (language === 'en'
                      ? `${num} is not prime because it has more than two divisors`
                      : `${num} n'est pas premier car il a plus de deux diviseurs`)
              };
            }
          },
          {
            generate: () => ({
              question: language === 'en'
                ? 'Is 1 a prime number?'
                : '1 est-il un nombre premier ?',
              correctAnswer: 0,
              explanation: language === 'en'
                ? '1 is not prime because it has only one divisor'
                : '1 n\'est pas premier car il n\'a qu\'un seul diviseur'
            })
          },
          {
            generate: () => ({
              question: language === 'en'
                ? 'Can a prime number be negative?'
                : 'Un nombre premier peut-il être négatif ?',
              correctAnswer: 0,
              explanation: language === 'en'
                ? 'Prime numbers are always positive'
                : 'Les nombres premiers sont toujours positifs'
            })
          }
        ];

        const selectedQuestion = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const { question, correctAnswer, explanation } = selectedQuestion.generate();

        return {
          type,
          question,
          correctAnswer,
          explanation
        };
      }

      case 'consecutive_primes': {
        const start = Math.floor(Math.random() * 20) + 2;
        const primes = [];
        let current = start;
        while (primes.length < 3) {
          if (isPrime(current)) {
            primes.push(current);
          }
          current++;
        }
        return {
          type,
          question: language === 'en'
            ? `What are the first three prime numbers after ${start - 1}?`
            : `Quels sont les trois premiers nombres premiers après ${start - 1} ?`,
          correctAnswer: primes,
          explanation: language === 'en'
            ? `The next three prime numbers are ${primes.join(', ')}`
            : `Les trois prochains nombres premiers sont ${primes.join(', ')}`
        };
      }

      default: {
        const start = Math.floor(Math.random() * 40) + 2;
        const next = getNextPrime(start);
        return {
          type: 'next_prime',
          question: language === 'en'
            ? `What is the next prime number after ${start}?`
            : `Quel est le prochain nombre premier après ${start} ?`,
          correctAnswer: next,
          explanation: language === 'en'
            ? `${next} is the next prime number after ${start}`
            : `${next} est le prochain nombre premier après ${start}`
        };
      }
    }
  };

  useEffect(() => {
    setCurrentQuestion(generateQuestion());
    
    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          setGameOver(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleAnswer = async (answer: number | number[]) => {
    if (showFeedback || !currentQuestion) return;

    setUserAnswer(answer);
    setShowFeedback(true);
    
    let correct = false;
    if (Array.isArray(answer) && Array.isArray(currentQuestion.correctAnswer)) {
      correct = answer.length === currentQuestion.correctAnswer.length &&
        answer.every((num, index) => num === currentQuestion.correctAnswer[index]);
    } else {
      correct = answer === currentQuestion.correctAnswer;
    }

    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);

    if (correct) {
      setScore(score + 1);
    } else {
      playError();
    }

    setTimeout(() => {
      if (questionNumber < 10 && remainingTime > 0) {
        setQuestionNumber(questionNumber + 1);
        setCurrentQuestion(generateQuestion());
        setUserAnswer(null);
        setShowFeedback(false);
        setConsecutiveAnswers(['', '', '']);
        setInputValue('');
      } else {
        setGameOver(true);
        saveProgress(newStats);
      }
    }, 1500);
  };

  const handleConsecutiveInput = (index: number, value: string) => {
    const newAnswers = [...consecutiveAnswers];
    newAnswers[index] = value;
    setConsecutiveAnswers(newAnswers);

    if (newAnswers.every(ans => ans !== '')) {
      const numbers = newAnswers.map(ans => parseInt(ans, 10));
      if (numbers.every(num => !isNaN(num))) {
        handleAnswer(numbers);
      }
    }
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'prime',
        mode: 'quiz',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: (60 - remainingTime) / currentStats.questionsAnswered,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const formatTime = (seconds: number): string => {
    return `${seconds}s`;
  };

  if (!currentQuestion) return null;

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 ${
            remainingTime <= 10 
              ? 'text-red-600 dark:text-red-400' 
              : 'text-indigo-600 dark:text-indigo-400'
          }`}>
            <Timer className="h-5 w-5" />
            <span className="font-bold">{formatTime(remainingTime)}</span>
          </div>
          <div className="text-slate-700 dark:text-slate-300">
            {questionNumber}/10
          </div>
        </div>
      </div>

      {gameOver ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Quiz Complete!' : 'Quiz terminé !'}
          </h2>
          <div className="mb-8">
            <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
              {score}/10
            </div>
            <div className="text-slate-600 dark:text-slate-300">
              {language === 'en' ? 'Final Score' : 'Score final'}
            </div>
          </div>
          <button
            onClick={() => {
              setQuestionNumber(1);
              setScore(0);
              setGameOver(false);
              setRemainingTime(60);
              setCurrentQuestion(generateQuestion());
              setUserAnswer(null);
              setShowFeedback(false);
              setStats({
                questionsAnswered: 0,
                correctAnswers: 0,
                incorrectAnswers: 0,
                totalTime: 0,
                averageResponseTime: 0,
              });
            }}
            className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
              hover:bg-indigo-700 dark:hover:bg-indigo-600 
              focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {currentQuestion.question}
            </h2>
          </div>

          <div className="flex flex-col items-center gap-4">
            {currentQuestion.type === 'multiple_choice' && currentQuestion.options && (
              <div className="grid grid-cols-2 gap-4 w-full">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(option)}
                    disabled={showFeedback}
                    className={`p-4 text-lg font-medium rounded-lg transition-all
                      ${showFeedback
                        ? option === currentQuestion.correctAnswer
                          ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-500/50'
                          : option === userAnswer
                          ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-2 border-red-500 dark:border-red-500/50'
                          : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                        : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:text-indigo-600 dark:hover:text-indigo-300'
                      }
                    `}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            {currentQuestion.type === 'true_false' && (
              <div className="grid grid-cols-2 gap-4 w-full">
                <button
                  onClick={() => handleAnswer(1)}
                  disabled={showFeedback}
                  className={`p-4 text-lg font-medium rounded-lg transition-all
                    ${showFeedback
                      ? 1 === currentQuestion.correctAnswer
                        ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-500/50'
                        : 1 === userAnswer
                        ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-2 border-red-500 dark:border-red-500/50'
                        : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                      : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:text-indigo-600 dark:hover:text-indigo-300'
                    }
                  `}
                >
                  {language === 'en' ? 'True' : 'Vrai'}
                </button>
                <button
                  onClick={() => handleAnswer(0)}
                  disabled={showFeedback}
                  className={`p-4 text-lg font-medium rounded-lg transition-all
                    ${showFeedback
                      ? 0 === currentQuestion.correctAnswer
                        ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-500/50'
                        : 0 === userAnswer
                        ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-2 border-red-500 dark:border-red-500/50'
                        : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                      : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:text-indigo-600 dark:hover:text-indigo-300'
                    }
                  `}
                >
                  {language === 'en' ? 'False' : 'Faux'}
                </button>
              </div>
            )}

            {(currentQuestion.type === 'next_prime' || 
              currentQuestion.type === 'consecutive_primes') && (
              <div className="w-full max-w-md space-y-4">
                {currentQuestion.type === 'consecutive_primes' ? (
                  <div className="grid grid-cols-3 gap-4">
                    {[0, 1, 2].map((index) => (
                      <input
                        key={index}
                        type="number"
                        value={consecutiveAnswers[index]}
                        onChange={(e) => handleConsecutiveInput(index, e.target.value)}
                        placeholder={`${index + 1}${language === 'en' ? 'st' : 'er'}`}
                        className={`w-full p-4 text-lg font-medium rounded-lg border-2 text-center
                          bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                          focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400
                          ${showFeedback
                            ? Array.isArray(userAnswer) && Array.isArray(currentQuestion.correctAnswer) &&
                              userAnswer[index] === currentQuestion.correctAnswer[index]
                              ? 'border-green-500 dark:border-green-500/50 bg-green-50 dark:bg-green-900/30'
                              : 'border-red-500 dark:border-red-500/50 bg-red-50 dark:bg-red-900/30'
                            : 'border-slate-200 dark:border-slate-600'
                          }`}
                        disabled={showFeedback}
                      />
                    ))}
                  </div>
                ) : (
                  <input
                    type="number"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={language === 'en' ? 'Enter number' : 'Entrez un nombre'}
                    className={`w-full p-4 text-lg font-medium rounded-lg border-2 text-center
                      bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400
                      ${showFeedback
                        ? userAnswer === currentQuestion.correctAnswer
                          ? 'border-green-500 dark:border-green-500/50 bg-green-50 dark:bg-green-900/30'
                          : 'border-red-500 dark:border-red-500/50 bg-red-50 dark:bg-red-900/30'
                        : 'border-slate-200 dark:border-slate-600'
                      }`}
                    disabled={showFeedback}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const value = parseInt(inputValue);
                        if (!isNaN(value)) {
                          handleAnswer(value);
                        }
                      }
                    }}
                  />
                )}
                <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                  {language === 'en'
                    ? 'Press Enter to submit'
                    : 'Appuyez sur Entrée pour soumettre'}
                </p>
              </div>
            )}
          </div>

          {showFeedback && currentQuestion.explanation && (
            <div className="mt-4 text-center text-slate-700 dark:text-slate-300">
              {currentQuestion.explanation}
            </div>
          )}

          <div className="mt-6 text-sm text-slate-600 dark:text-slate-400">
            <div className="flex justify-between">
              <span>{language === 'en' ? 'Question: ' : 'Question : '}{questionNumber}/10</span>
              <span className="text-green-600">{language === 'en' ? 'Correct' : 'Correct'}: {score}</span>
              <span className={`font-bold ${(score / 10) * 100 < 60 ? 'text-red-500' : 'text-green-500'}`}>
                {language === 'en' ? 'Accuracy' : 'Précision'}: {Math.round((score / 10) * 100)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrimeQuizGame;